import { Component, OnDestroy, effect, inject } from '@angular/core';
import { ThemeService } from '../../core/services/theme.service';
import 'particles.js';

declare const particlesJS: (tagId: string, config: object) => void;
declare global {
  interface Window {
    pJSDom?: Array<{ pJS: { fn: { vendors: { destroypJS: () => void } } } }>;
  }
  interface ObjectConstructor {
    deepExtend?: (destination: unknown, source: unknown) => unknown;
  }
}

/**
 * particles.js (2015) recurre usando `arguments.callee`, prohibido en modo
 * estricto. Los bundles de Angular son módulos ES = siempre estricto, así que
 * la librería original crashea en silencio al inicializar. Sobrescribimos su
 * `Object.deepExtend` con una versión recursiva equivalente pero sin `callee`.
 */
function patchDeepExtend(): void {
  Object.deepExtend = function deepExtend(destination: any, source: any): unknown {
    for (const property in source) {
      if (source[property] && source[property].constructor === Object) {
        destination[property] = destination[property] || {};
        deepExtend(destination[property], source[property]);
      } else {
        destination[property] = source[property];
      }
    }
    return destination;
  };
}

const PALETTES = {
  light: {
    dots: ['#2a6f77', '#63a04b', '#d09010', '#16283c'],
    lines: '#2a6f77',
    lineOpacity: 0.18,
  },
  dark: {
    dots: ['#2b9d99', '#63a04b', '#e8a922', '#8fb3d9'],
    lines: '#e8a922',
    lineOpacity: 0.22,
  },
};

@Component({
  selector: 'app-particles',
  standalone: true,
  template: '<div id="particles-js" class="particles"></div>',
  styles: `
    :host {
      position: fixed;
      inset: 0;
      height: 100dvh;
      z-index: 0;
      pointer-events: none;
    }

    .particles {
      position: absolute;
      inset: 0;
    }
  `,
})
export class ParticlesComponent implements OnDestroy {
  private readonly theme = inject(ThemeService);

  constructor() {
    // Re-inicializa las partículas cada vez que cambia el tema
    effect(() => {
      const palette = PALETTES[this.theme.theme()];
      this._destroy();
      // El div #particles-js ya existe cuando el effect corre tras el primer render
      queueMicrotask(() => this._init(palette));
    });
  }

  ngOnDestroy(): void {
    this._destroy();
  }

  private _init(palette: (typeof PALETTES)['light']): void {
    if (!document.getElementById('particles-js')) return;
    patchDeepExtend();
    particlesJS('particles-js', {
      particles: {
        number: { value: 90, density: { enable: true, value_area: 900 } },
        color: { value: palette.dots },
        shape: { type: 'circle' },
        opacity: { value: 0.7, random: true },
        size: { value: 3.5, random: true },
        line_linked: {
          enable: true,
          distance: 140,
          color: palette.lines,
          opacity: palette.lineOpacity,
          width: 1,
        },
        move: {
          enable: true,
          speed: 1.2,
          direction: 'none',
          random: false,
          straight: false,
          out_mode: 'out',
        },
      },
      interactivity: {
        // 'window' en vez de 'canvas': el canvas tiene pointer-events:none
        // (para no bloquear clics en el contenido de encima), así que la
        // detección de mouse se hace a nivel de ventana.
        detect_on: 'window',
        events: {
          onhover: { enable: true, mode: 'grab' },
          onclick: { enable: true, mode: 'push' },
          resize: true,
        },
        modes: {
          grab: { distance: 160, line_linked: { opacity: 0.5 } },
          push: { particles_nb: 3 },
        },
      },
      retina_detect: true,
    });
  }

  private _destroy(): void {
    window.pJSDom?.forEach((dom) => dom.pJS.fn.vendors.destroypJS());
    window.pJSDom = [];
  }
}
